import { useEffect, useMemo, useRef, useState } from "react";
import { FullScreenHandle } from "react-full-screen";
import { useTranslation } from "react-i18next";
import {
  MdCloseFullscreen,
  MdMusicNote,
  MdMusicOff,
  MdOpenInFull,
  MdVolumeOff,
  MdVolumeUp,
} from "react-icons/md";
import { User, User as UserModel } from "../../../../models/user";
import * as UserApi from "../../../../network/user_api";
import "../../GamePage.scss";
import { getLevelFromXp } from "../../../../utils/levelingSystem";
import { useGameStore } from "../../stores/gameStore";
import LevelUpModal from "../../../../components/Modals/LevelUpModal";
import ActionButton from "../../../../components/Buttons/Action/ActionButton";
import Divider from "../../../../components/Decors/Divider/Divider";
import { GameStates } from "../../constants/GameStates";
import { SoundPaths } from "../../constants/SoundPaths";
import { GameModes } from "../../constants/GameModes";
import { useNavigate } from "react-router-dom";
import * as BadgesApi from "../../../../network/collectibles_api";
import BadgeModal from "../../../../components/Modals/BadgeModal";

interface MenuProps {
  handle: FullScreenHandle;
  user: UserModel;
  onUserXpSaved: (user: UserModel) => void;
}

const Menu = ({ handle, user, onUserXpSaved }: MenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [xpUpdateInProgress, setXpUpdateInProgress] = useState(false);
  const xpUpdateCalledRef = useRef(false); // Track if XP update has been called
  const [answerPoints, setAnswerPoints] = useState(0);
  const [streakPoints, setStreakPoints] = useState(0);
  const [finalXP, setFinalXP] = useState(0);
  const [newBadge, setNewBadge] = useState("");

  const {
    loadNewGame,
    startGame,
    gameState,
    goodAnswers,
    goodAnswerStreak,
    answerQuantity,
    coins,
    gameSetId,
  } = useGameStore((state: any) => ({
    loadNewGame: state.loadNewGame,
    startGame: state.startGame,
    gameState: state.gameState,
    goodAnswers: state.goodAnswers,
    goodAnswerStreak: state.goodAnswerStreak,
    answerQuantity: state.answerQuantity,
    coins: state.coins,
    gameSetId: state.gameSetId,
  }));

  const { isSoundOn, setIsSoundOn } = useGameStore((state: any) => ({
    isSoundOn: state.isSoundOn,
    setIsSoundOn: state.setIsSoundOn,
  }));

  const { isMusicOn, setIsMusicOn } = useGameStore((state: any) => ({
    isMusicOn: state.isMusicOn,
    setIsMusicOn: state.setIsMusicOn,
  }));

  const { isFullscreen, setIsFullscreen } = useGameStore((state: any) => ({
    isFullscreen: state.isFullscreen,
    setIsFullscreen: state.setIsFullscreen,
  }));

  // To show cursor
  document.exitPointerLock();

  /**
   * Updates the user's XP by sending a request to the API.
   * Ensures that the XP update process is not triggered multiple times concurrently.
   * If the XP update is successful, it checks if the user's level has changed.
   *
   * @param {User} data - The user data to update the XP with.
   */
  async function updateUserXP(data: User) {
    if (xpUpdateInProgress) return;

    try {
      setXpUpdateInProgress(true);
      let userResponse: UserModel;

      if (user && data) {
        userResponse = await UserApi.updateUser(data);
        checkIfLevelChanged(getLevelFromXp(userResponse.xp).level, user.level);
        onUserXpSaved(userResponse);
      }
    } catch (error) {
      console.error("Failed to update XP:", error);
      alert(error);
    } finally {
      setXpUpdateInProgress(false);
    }
  }

  /**
   * Plays an audio file from the provided path with the given playback speed.
   *
   * @param {string} path - The path to the audio file (excluding extension).
   * @param {number} playbackSpeed - The speed at which the audio should be played.
   */
  const playAudio = (path: string, playbackSpeed: number) => {
    const audio = new Audio(`/sounds/${path}.wav`);
    audio.playbackRate = playbackSpeed;
    audio.play();
  };

  /**
   * Checks if the user's level has changed after an XP update.
   * Checks and updates level based badges.
   * If the level has changed, it triggers the display of the level-up modal.
   *
   * @param {number} newLevel - The new level of the user.
   * @param {number} currentLevel - The current level of the user.
   */
  const checkIfLevelChanged = (newLevel: number, currentLevel: number) => {
    if (newLevel !== currentLevel) {
      addLevelBadge(newLevel);
      setShowLevelUpModal(true);
    }
  };

  /**
   * Calculates the XP based on the user's answers and streak.
   * - Answer points are calculated as a percentage of correct answers.
   * - Streak points are awarded for maintaining a correct answer streak.
   *
   * Stores the calculated XP values in state.
   *
   * @returns {number} The total calculated XP (answer points + streak points).
   */
  const calculateXp = () => {
    const newAnswerPoints = (goodAnswers / answerQuantity) * 100;
    let newStreakPoints = 0;
    if (goodAnswerStreak === 3) {
      newStreakPoints = 15;
    } else if (goodAnswerStreak === 4) {
      newStreakPoints = 20;
    } else if (goodAnswerStreak >= 5) {
      newStreakPoints = 25;
    }
    const totalXp = newAnswerPoints + newStreakPoints;

    setFinalXP(totalXp);
    setAnswerPoints(newAnswerPoints);
    setStreakPoints(newStreakPoints);

    return totalXp;
  };

  /**
   * Checks if the user has the "First Encounter" badge. If not, creates it and shows the badge modal.
   *
   * @returns {Promise<void>} Resolves when the badge check and creation process is complete.
   */
  async function checkUserHasFirstEncounterBadge(): Promise<void> {
    try {
      const badges = await BadgesApi.fetchBadges();
      const hasFirstEncounter = badges.some(
        (badge) => badge.fileName === "firstEncounter"
      );
      if (!hasFirstEncounter) {
        await BadgesApi.createBadge({
          fileName: "firstEncounter",
          englishTitle: "First Encounter",
          hungarianTitle: "Első találkozás",
          englishRequirement: "Win your first game",
          hungarianRequirement: "Nyerd meg az első játékod",
        });
      }
      setNewBadge("firstEncounter");
      setShowBadgeModal(true);
    } catch (error) {
      console.error("Error updating badge:", error);
    }
  }

  /**
   * Creates a level-based badge for the user when they reach specific levels (5, 10, 25, 50) and displays the badge modal.
   *
   * @param {number} level - The level the user has reached.
   * @returns {Promise<void>} Resolves when the level badge creation process is complete.
   */
  async function addLevelBadge(level: number): Promise<void> {
    try {
      if (level === 5) {
        await BadgesApi.createBadge({
          fileName: "shardbearer",
          englishTitle: "Shardbearer",
          hungarianTitle: "Töredékőrző",
          englishRequirement: `Reach level ${level}`,
          hungarianRequirement: `Érd el az ${level} szintet`,
        });
        setNewBadge("shardbearer");
      } else if (level === 10) {
        await BadgesApi.createBadge({
          fileName: "wisdombound",
          englishTitle: "Wisdombound",
          hungarianTitle: "Bölcsességvigyázó",
          englishRequirement: `Reach level ${level}`,
          hungarianRequirement: `Érd el az ${level} szintet`,
        });
        setNewBadge("wisdombound");
      } else if (level === 25) {
        await BadgesApi.createBadge({
          fileName: "truthforged",
          englishTitle: "Truthforged",
          hungarianTitle: "Igazságkovács",
          englishRequirement: `Reach level ${level}`,
          hungarianRequirement: `Érd el az ${level} szintet`,
        });
        setNewBadge("truchforged");
      } else if (level === 50) {
        await BadgesApi.createBadge({
          fileName: "tomewarden",
          englishTitle: "Tomewarden",
          hungarianTitle: "Kódexőr",
          englishRequirement: `Reach level ${level}`,
          hungarianRequirement: `Érd el az ${level} szintet`,
        });
        setNewBadge("tomewarden");
      }
      setShowBadgeModal(true);
    } catch (error) {
      console.error("Error updating badge:", error);
    }
  }

  /**
   * Creates a streak badge for the user based on the provided streak number and displays the badge modal.
   *
   * @param {number} streak - The number of days in the streak.
   * @returns {Promise<void>} Resolves when the streak badge creation process is complete.
   */
  async function addStreakBadge(streak: number): Promise<void> {
    try {
      await BadgesApi.createBadge({
        fileName: `${streak}dayStreak`,
        englishTitle: `${streak} day streak`,
        hungarianTitle: `${streak} napos streak`,
        englishRequirement: `Play with Solvize for ${streak} days`,
        hungarianRequirement: `Játsz Solvize-zal ${streak} nap`,
      });
      setNewBadge(`${streak}dayStreak`);
      setShowBadgeModal(true);
    } catch (error) {
      console.error("Error updating badge:", error);
    }
  }

  /**
   * Triggers the check for the "First Encounter" badge when the user wins a game.
   */
  useEffect(() => {
    if (gameState === GameStates.WON) {
      checkUserHasFirstEncounterBadge();
    }
  }, [gameState]);

  /**
   * useEffect to handle sound effects based on the game state.
   * Plays the appropriate sound effect when the game is won or lost.
   */
  useEffect(() => {
    if (gameState === GameStates.WON && isSoundOn) {
      playAudio(SoundPaths.WON_FX, 1.3);
    } else if (gameState === GameStates.GAME_OVER && isSoundOn) {
      playAudio(SoundPaths.GAME_OVER_FX, 1);
    }
  }, [gameState, isSoundOn]);

  /**
   * useEffect to handle XP and streak update after the game is won or lost.
   * The XP update is called only once (tracked by xpUpdateCalledRef).
   * It calculates the user's XP and updates the user data.
   */
  useEffect(() => {
    if (
      (gameState === GameStates.WON || gameState === GameStates.GAME_OVER) &&
      !xpUpdateCalledRef.current
    ) {
      xpUpdateCalledRef.current = true;
      const calculatedXp = Math.floor(calculateXp());
      const calculatedCoins = Math.floor(coins + goodAnswers * 2);

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize to midnight
      const lastActiveDate = new Date(user.lastActiveDate!);
      lastActiveDate.setHours(0, 0, 0, 0);

      const timeDiff = today.getTime() - lastActiveDate.getTime();
      const oneDayInMs = 24 * 60 * 60 * 1000; // 1 day in milliseconds

      let newStreak = 1;

      // Compare timestamps, not objects
      if (today.getTime() === lastActiveDate.getTime()) {
        newStreak = user.streak; // Keep streak
      } else if (timeDiff === oneDayInMs) {
        newStreak = user.streak + 1; // Increase streak by 1
      }

      if (
        newStreak === 7 ||
        newStreak === 14 ||
        newStreak === 30 ||
        newStreak === 60 ||
        newStreak === 100
      ) {
        addStreakBadge(newStreak);
      }

      const updatedUser = {
        ...user,
        xp: calculatedXp,
        coin: user.coin + calculatedCoins,
        lastActiveDate: today,
        streak: newStreak,
      };
      updateUserXP(updatedUser);
    }
  }, [gameState]);

  return (
    <>
      <LevelUpModal
        newLevel={user.level}
        show={showLevelUpModal}
        onDismiss={() => setShowLevelUpModal(false)}
      />
      <BadgeModal
        newBadge={newBadge}
        show={showBadgeModal}
        onDismiss={() => setShowBadgeModal(false)}
      />
      <div className={"menu"}>
        {gameState === GameStates.MENU ? (
          <>
            <h1>{t("game.starter")}</h1>
            <Divider />
            <h2>{t("game.settings")}</h2>
            <div className="menu-container">
              <p>{t("game.sounds")}</p>
              <MdVolumeUp
                className={`menu-icon ${isSoundOn ? "chosen" : ""}`}
                onClick={() => {
                  setIsSoundOn(true);
                }}
              />
              <MdVolumeOff
                className={`menu-icon ${isSoundOn ? "" : "chosen"}`}
                onClick={() => setIsSoundOn(false)}
              />
            </div>
            <div className="menu-container">
              <p>{t("game.music")}</p>
              <MdMusicNote
                className={`menu-icon ${isMusicOn ? "chosen" : ""}`}
                onClick={() => setIsMusicOn(true)}
              />
              <MdMusicOff
                className={`menu-icon ${isMusicOn ? "" : "chosen"}`}
                onClick={() => setIsMusicOn(false)}
              />
            </div>
            <div className="menu-container">
              <p>{t("game.fullScreen")}</p>
              <MdOpenInFull
                className={`menu-icon ${isFullscreen ? "chosen" : ""}`}
                onClick={() => {
                  setIsFullscreen(true);
                  handle.enter();
                }}
              />
              <MdCloseFullscreen
                className={`menu-icon ${isFullscreen ? "" : "chosen"}`}
                onClick={() => {
                  setIsFullscreen(false);
                  handle.exit();
                }}
              />
            </div>
            <h2>{t("game.mode")}</h2>
            <div className="buttons-container">
              <ActionButton
                className="menu-button"
                disabled={gameState !== GameStates.MENU}
                onClick={() =>
                  startGame(GameModes.LEARN, isSoundOn, isMusicOn, isFullscreen)
                }
              >
                {t("game.learn")}
              </ActionButton>
              <ActionButton
                className="menu-button"
                disabled={gameState !== GameStates.MENU}
                onClick={() =>
                  startGame(GameModes.TEST, isSoundOn, isMusicOn, isFullscreen)
                }
              >
                {t("game.test")}
              </ActionButton>
            </div>
          </>
        ) : null}
        {gameState === GameStates.WON && (
          <>
            <h1>{t("game.won")}</h1>
            <div style={{ width: "100%" }}>
              <Divider />
            </div>
            <h2>
              {t("game.finalScore")}
              {finalXP}XP
            </h2>
            <p className="scores">
              {t("game.correctAnswers")}: {`${goodAnswers} / ${answerQuantity}`}{" "}
              <br />
              {t("game.answerPoints")}: {answerPoints} <br />
              {t("game.streakPoints")}: {streakPoints} <br />
              {t("game.coins")}: {Math.floor(coins + goodAnswers * 2)}
            </p>
            <ActionButton onClick={() => loadNewGame(gameSetId)}>
              {t("game.reloadGame")}
            </ActionButton>
            <ActionButton
              onClick={() => {
                navigate(`/gameSets`);
              }}
            >
              {t("common.back")}
            </ActionButton>
          </>
        )}
        {gameState === GameStates.GAME_OVER && (
          <>
            <h1>{t("game.gameover")}</h1>
            <Divider />
            <h2>
              {t("game.finalScore")}
              {finalXP}XP
            </h2>
            <p className="scores">
              {t("game.correctAnswers")}: {`${goodAnswers} / ${answerQuantity}`}{" "}
              <br />
              {t("game.answerPoints")}: {answerPoints} <br />
              {t("game.streakPoints")}: {streakPoints}
            </p>
            <ActionButton onClick={() => loadNewGame(gameSetId)}>
              {t("game.reloadGame")}
            </ActionButton>
            <ActionButton
              onClick={() => {
                navigate(`/gameSets`);
              }}
            >
              {t("common.back")}
            </ActionButton>
          </>
        )}
      </div>
    </>
  );
};

export default Menu;
